<template>
    <div id="story" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
        <top-bar :page="'story'" :leftText="story.name" v-if="story" />

        <div id="sidebar">
            <div class="d-flex align-items-center mb-3">
                <!-- Toggle Button -->
                <button
                    class="btn btn-sm btn-outline-light me-2"
                    @click="toggleSidebar"
                    title="Toggle Sidebar"
                >
                    <font-awesome-icon
                        :icon="
                            isSidebarCollapsed
                                ? 'chevron-right'
                                : 'chevron-left'
                        "
                    />
                </button>

                <!-- Add Encounter Button (hidden when collapsed) -->
                <div v-if="!isSidebarCollapsed" class="d-grid flex-fill">
                    <button
                        class="btn btn-primary text-center"
                        data-bs-toggle="modal"
                        data-bs-target="#encounterModal"
                        aria-controls="encounterModal"
                    >
                        <font-awesome-icon icon="plus" /> Add Encounter
                    </button>
                </div>
            </div>

            <div v-if="story.encounters">
                <div
                    class="card bg-dark text-light mb-3"
                    v-for="(encounter, index) in story.encounters"
                    :key="encounter.id"
                    :class="{ 'active-story': encounter.id === encounterIndex }"
                    @click="
                        encounterToView = encounter;
                        encounterIndex = encounter.id;
                        activeTab = 'info';
                    "
                    :title="isSidebarCollapsed ? encounter.name : ''"
                >
                    <div
                        class="card-body d-flex flex-column align-items-stretch"
                    >
                        <!-- Show number when collapsed, name when expanded -->
                        <h5 class="card-title mb-2 text-center">
                            {{
                                isSidebarCollapsed ? index + 1 : encounter.name
                            }}
                        </h5>

                        <!-- Up/Down Buttons (hidden when collapsed) -->
                        <div v-if="!isSidebarCollapsed" class="btn-group w-100">
                            <button
                                class="btn btn-sm btn-secondary w-50"
                                @click.stop="moveEncounter(index, -1)"
                                :disabled="index === 0"
                            >
                                ↑
                            </button>
                            <button
                                class="btn btn-sm btn-secondary w-50"
                                @click.stop="moveEncounter(index, 1)"
                                :disabled="
                                    index === story.encounters.length - 1
                                "
                            >
                                ↓
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div id="main" class="p-4">
            <div v-if="encounterToView">
                <div
                    class="d-flex justify-content-between align-items-center mb-3"
                >
                    <h3 class="mb-0">{{ encounterToView.name }}</h3>
                    <div>
                        <button
                            class="btn btn-sm btn-outline-danger"
                            @click="deleteEncounter(encounterToView.id)"
                        >
                            <font-awesome-icon icon="trash" /> Delete
                        </button>
                    </div>
                </div>

                <!-- 🧭 Tabs -->
                <ul class="nav nav-tabs">
                    <li class="nav-item">
                        <button
                            class="nav-link"
                            :class="{ active: activeTab === 'info' }"
                            @click="activeTab = 'info'"
                        >
                            <font-awesome-icon icon="info-circle" /> Info
                        </button>
                    </li>
                    <li class="nav-item">
                        <button
                            class="nav-link"
                            :class="{ active: activeTab === 'combat' }"
                            @click="activeTab = 'combat'"
                        >
                            <font-awesome-icon icon="skull-crossbones" /> Combat
                        </button>
                    </li>
                </ul>

                <!-- 🗒️ Info tab -->
                <div v-if="activeTab === 'info'" class="encounter-content">
                    <div class="d-flex justify-content-end mb-2">
                        <button
                            class="btn btn-sm btn-outline-info"
                            @click="openEncounterModal(encounterToView)"
                        >
                            <font-awesome-icon icon="pen" /> Edit
                        </button>
                    </div>
                    <div class="prose" v-html="encounterToView.content"></div>
                </div>

                <!-- ⚔️ Combat tab -->
                <div v-if="activeTab === 'combat'" class="encounter-content">
                    <monsters
                        :monsters="encounterToView.monsters"
                        :story-id="storyId"
                        :encounter-id="encounterToView.id"
                        :theme="theme"
                    />
                </div>
            </div>

            <div v-else class="text-center mt-5">
                <h2>
                    Select an encounter from the sidebar or create a new one.
                </h2>
            </div>
        </div>

        
            <DoomclockTracker
                v-if="story"
                :initial="story.doomclock"
                @update="updateDoomclock($event)"
            />
        
    </div>

    <dice-box
        v-if="story && diceRoller"
        ref="diceBox"
        :theme="theme"
        :clickedSkillKey="clickedSkillKey"
    />

    <!-- Encounter Modal -->
    <div
        class="modal fade"
        id="encounterModal"
        tabindex="-1"
        aria-labelledby="encounterModalLabel"
        aria-hidden="true"
    >
        <div class="modal-dialog modal-fullscreen">
            <div class="modal-content d-flex flex-column bg-dark text-light">
                <div class="modal-header">
                    <h5 class="modal-title" id="encounterModalLabel">
                        {{ modalTitle }}
                    </h5>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                    ></button>
                </div>

                <div
                    class="modal-body d-flex flex-column overflow-hidden flex-grow-1"
                >
                    <div class="mb-3 input-group flex-shrink-0">
                        <span class="input-group-text">Encounter Name</span>
                        <input
                            type="text"
                            class="form-control"
                            id="encounter-name"
                            v-model="encounterName"
                            placeholder="Enter encounter name"
                        />
                    </div>

                    <div class="flex-grow-1 d-flex flex-column overflow-hidden">
                        <label class="form-label">Encounter Content</label>
                        <div
                            class="border rounded bg-body flex-grow-1 d-flex flex-column overflow-auto"
                            :class="editorFontClass"
                        >
                            <TiptapEditor
                                v-model="encounterContent"
                                :key="
                                    editingEncounter
                                        ? editingEncounter.id
                                        : 'new'
                                "
                            />
                        </div>
                    </div>
                </div>

                <div class="modal-footer flex-shrink-0">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="createEncounter"
                    >
                        {{ editingEncounter ? "Save Changes" : "Create" }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TopBar from "./TopBar.vue";
import Doomclock from "./Doomclock.vue";
import TiptapEditor from "./TiptapEditor.vue";
import { useRosterStore } from "../stores/index";
import Monsters from "./Monsters.vue";

export default {
    name: "Story",
    components: {
        TopBar,
        DoomclockTracker: Doomclock,
        TiptapEditor,
        Monsters,
    },
    props: { id: { type: [Number, String], required: true } },
    data() {
        return {
            store: useRosterStore(),
            clickedSkillKey: "grit",
            encounterName: "",
            encounterContent: "",
            editingEncounter: null,
            encounterToView: null,
            encounterIndex: null,
            activeTab: "info",
            isSidebarCollapsed: false,
            diceRoller: true, // keep existing behavior (you can adjust if needed)
        };
    },
    mounted() {
        const modalEl = document.getElementById("encounterModal");
        modalEl?.addEventListener("shown.bs.modal", this.focusEditor);

        if (this.story?.encounters?.length) {
            const first = this.story.encounters[0];
            this.encounterToView = first;
            this.encounterIndex = first.id;
        }
    },
    computed: {
        theme() {
            return Number(this.store.theme) || 0;
        },
        storyId() {
            return Number(this.id);
        },
        story() {
            return this.store.getStoryById(this.storyId);
        },
        editorFontClass() {
            if (this.theme === 1) return "theme-font--special";
            if (this.theme === 2) return "theme-font--texturina";
            return "";
        },
        modalTitle() {
            return this.editingEncounter ? "Edit Encounter" : "New Encounter";
        },
    },
    methods: {
        toggleSidebar() {
            this.isSidebarCollapsed = !this.isSidebarCollapsed;
        },
        deleteEncounter(id) {
            if (!confirm("Are you sure you want to delete this encounter?"))
                return;

            const story = this.story;
            if (!story || !story.encounters) return;

            story.encounters = story.encounters.filter((e) => e.id !== id);
            this.store.updateStories(
                this.store.stories.findIndex((s) => s.id === story.id),
                story
            );

            if (this.encounterToView?.id === id) {
                this.encounterToView = null;
                this.encounterIndex = null;
                if (story.encounters.length > 0) {
                    const first = story.encounters[0];
                    this.encounterToView = first;
                    this.encounterIndex = first.id;
                }
            }
        },
        moveEncounter(index, direction) {
            const newIndex = index + direction;
            this.store.moveEncounterInStory(this.storyId, index, newIndex);

            if (this.encounterToView) {
                const stillThere = this.story.encounters.find(
                    (e) => e.id === this.encounterToView.id
                );
                if (stillThere) {
                    this.encounterToView = stillThere;
                    this.encounterIndex = stillThere.id;
                }
            }
        },
        updateDoomclock(n) {
            this.store.updateStoryDoomclock(this.storyId, Number(n));
        },
        focusEditor() {
            const ed = document.querySelector("#encounterModal .ProseMirror");
            if (ed) ed.focus();
        },
        openEncounterModal(encounter = null) {
            this.editingEncounter = encounter;
            this.encounterName = encounter ? encounter.name : "";
            this.encounterContent = encounter ? encounter.content || "" : "";

            const el = document.getElementById("encounterModal");
            const modal =
                window.bootstrap.Modal.getInstance(el) ||
                new window.bootstrap.Modal(el);
            modal.show();
        },
        createEncounter() {
            const name = this.encounterName.trim();
            if (!name) return alert("Encounter name cannot be empty.");

            if (this.editingEncounter) {
                const updated = {
                    ...this.editingEncounter,
                    name,
                    content: this.encounterContent,
                };
                this.store.updateEncounter(this.storyId, updated);
                this.encounterToView = updated;
                this.encounterIndex = updated.id;
            } else {
                const newEncounter = {
                    id: Date.now(),
                    name,
                    content: this.encounterContent,
                    monsters: [],
                };
                this.store.addEncounter(this.storyId, newEncounter);
                this.encounterToView = newEncounter;
                this.encounterIndex = newEncounter.id;
            }

            this.resetEncounterForm();

            const el = document.getElementById("encounterModal");
            const modal =
                window.bootstrap.Modal.getInstance(el) ||
                new window.bootstrap.Modal(el);
            modal.hide();
        },
        resetEncounterForm() {
            this.encounterName = "";
            this.encounterContent = "";
            this.editingEncounter = null;
        },
    },
};
</script>

<style scoped>
/* --- LAYOUT ROOT --- */
#story {
    background-color: transparent;
    height: 100vh;
    display: grid;
    grid-template-columns: 1fr 3fr;
    grid-template-rows: min-content 1fr;
    grid-template-areas:
        "nav nav"
        "sidebar main";
    color: white;
    gap: 0;
    padding: 0;
    overflow: hidden; /* prevent global page scroll */
}

#story.sidebar-collapsed {
    grid-template-columns: 80px calc(100% - 80px);
}

/* ACTIVE STORY VISUALS */
.active-story {
    border: 2px solid #00bfff !important;
    background-color: rgba(0, 191, 255, 0.15) !important;
}
.active-story:hover {
    background-color: rgba(0, 191, 255, 0.25) !important;
}

/* --- SIDEBAR --- */
#sidebar {
    grid-area: sidebar;
    overflow-y: auto;
    max-height: 100vh;
    padding: 1rem 1rem 6rem 1rem; /* space for doomclock overlap */
    background-color: rgba(70, 70, 70, 0.6);
    transition: all 0.22s ease;
}

#story.sidebar-collapsed #sidebar {
    padding: 0.75rem 0.4rem 6rem 0.4rem;
}

#sidebar .card {
    transition: all 0.22s ease;
    overflow: hidden;
}
#story.sidebar-collapsed #sidebar .card .card-body {
    padding-top: 0.375rem;
    padding-bottom: 0.375rem;
}
#story.sidebar-collapsed #sidebar .card .card-title {
    text-align: center;
    font-weight: 600;
    font-size: 0.95rem;
}

/* --- MAIN --- */
/* main is column flex; min-height:0 allows children to shrink for scroll to work */
#main {
    grid-area: main;
    display: flex;
    flex-direction: column;
    min-height: 0; /* critical to allow children to shrink inside flex */
    padding: 1rem !important;
    overflow: hidden; /* prevent #main from scrolling */
    box-sizing: border-box;
}

/* The immediate child of #main (the v-if wrapper) must be a flex column too.
   This selector targets the element that wraps header + tabs + content. */
#main > div {
    display: flex;
    flex-direction: column;
    min-height: 0; /* critical */
    flex: 1 1 auto;
    box-sizing: border-box;
}

/* Keep the header (title + tabs) natural height */
#main > div > .d-flex:first-child,
#main > div > .nav {
    flex: 0 0 auto;
}

/* --- ENCOUNTER CONTENT (scrollable area) --- */
/* In your version .encounter-content is the inner area, make it the only element that scrolls */
.encounter-content {
    flex: 1 1 auto; /* fill remaining space inside the #main > div wrapper */
    min-height: 0; /* CRITICAL to allow overflow to work in flex children */
    overflow-y: auto; /* only this scrolls */
    /* padding: 1.5rem; */
    color: #fff;
    background-color: rgba(255, 255, 255, 0.05);
    border-left: 1px solid #444;
    border-right: 1px solid #444;
    border-bottom: 1px solid #444;
    border-radius: 0 0 8px 8px;
    box-sizing: border-box;
}

/* Let .prose flow inside .encounter-content; don't force another scroll area */
.encounter-content .prose {
    line-height: 1.6;
    padding: 1.5rem;
    padding-bottom: 1rem; /* ensures final text can scroll under doomclock */
    overflow: visible;
}

/* images inside content */
.encounter-content img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 1rem 0;
}



/* --- NAV TABS --- */
.nav-tabs {
    border-bottom: 1px solid #444;
}
.nav-tabs .nav-link {
    color: #ccc;
    background: transparent;
    border: none;
}
.nav-tabs .nav-link.active {
    background: #222;
    color: #00bfff;
    border-radius: 6px 6px 0 0;
    border: 1px solid #444;
    border-bottom: none;
}

/* --- TIPTAP IMAGE / BLOCKQUOTE RULES (unchanged) --- */
:deep(.encounter-content .prose .image-node-wrap) {
    display: block;
    max-width: 100%;
    text-align: center;
    line-height: 0;
}
:deep(.encounter-content .prose .image-node-wrap img) {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 1rem auto;
}
:deep(.encounter-content .prose blockquote) {
    background: url("@/../public/parchment.jpg") center/cover repeat;
    padding: 1.5rem 2rem;
    margin: 1.5rem auto;
    max-width: 80%;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.25);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
    color: #111;
    font-size: 1.1rem;
    text-align: center;
    position: relative;
}
:deep(.encounter-content .prose blockquote::before) {
    content: "“";
    font-size: 3rem;
    color: rgba(0, 0, 0, 0.4);
    position: absolute;
    top: 0.4rem;
    left: 0.8rem;
}
:deep(.encounter-content .prose blockquote p) {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.5;
}

/* fallback */
:deep(.encounter-content .prose img) {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 1rem auto;
}

/* sidebar toggle nicety */
#sidebar .btn-outline-light {
    min-width: 36px;
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
</style>

